/**
 * Live byte progress of composer uploads, keyed by upload id (#4809). Kept
 * outside React state so a 380 MB upload's hundreds of progress events only
 * re-render the one chip that shows them.
 */

/** No new bytes for this long while sending means the upload stalled. */
export const COMPOSER_UPLOAD_STALL_MS = 20_000;

export type ComposerUploadProgress = {
  loadedBytes: number;
  totalBytes: number;
  /** `finishing`: every byte is sent; the server is verifying and filing it. */
  phase: 'sending' | 'finishing';
  stalled: boolean;
  /** When bytes last moved (or the phase last changed). */
  updatedAt: number;
};

export function createComposerUploadProgressStore(
  now: () => number = Date.now
) {
  const entries = new Map<string, ComposerUploadProgress>();
  const listeners = new Set<() => void>();
  const emit = () => {
    for (const listener of listeners) listener();
  };
  return {
    get: (uploadId: string) => entries.get(uploadId),
    /** Bytes moved: clears any stall. */
    sent(uploadId: string, loadedBytes: number, totalBytes: number) {
      const previous = entries.get(uploadId);
      if (
        previous &&
        previous.phase === 'sending' &&
        !previous.stalled &&
        previous.loadedBytes === loadedBytes &&
        previous.totalBytes === totalBytes
      )
        return;
      entries.set(uploadId, {
        loadedBytes,
        totalBytes,
        phase: 'sending',
        stalled: false,
        updatedAt:
          previous && previous.loadedBytes === loadedBytes
            ? previous.updatedAt
            : now(),
      });
      emit();
    },
    finishing(uploadId: string) {
      const previous = entries.get(uploadId);
      if (previous?.phase === 'finishing') return;
      entries.set(uploadId, {
        loadedBytes: previous?.totalBytes ?? previous?.loadedBytes ?? 0,
        totalBytes: previous?.totalBytes ?? 0,
        phase: 'finishing',
        stalled: false,
        updatedAt: now(),
      });
      emit();
    },
    /** Marks sending uploads with no new bytes for `stallMs`; returns their ids. */
    sweepStalls(stallMs: number = COMPOSER_UPLOAD_STALL_MS): string[] {
      const stalled: string[] = [];
      const at = now();
      for (const [uploadId, entry] of entries) {
        if (entry.phase !== 'sending' || entry.stalled) continue;
        if (at - entry.updatedAt < stallMs) continue;
        entries.set(uploadId, { ...entry, stalled: true });
        stalled.push(uploadId);
      }
      if (stalled.length) emit();
      return stalled;
    },
    clear(uploadId: string) {
      if (entries.delete(uploadId)) emit();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export type ComposerUploadProgressStore = ReturnType<
  typeof createComposerUploadProgressStore
>;

/** The one store every composer on the page reports into. */
export const composerUploadProgress = createComposerUploadProgressStore();

const MB = 1000 * 1000;

/** "42%" and "160 / 383 MB" (whole MB from 10 MB up, one decimal below). */
export function formatComposerUploadProgress(
  progress: Pick<ComposerUploadProgress, 'loadedBytes' | 'totalBytes'>,
  locale?: string
): { percent: string; bytes: string } {
  const total = Math.max(progress.totalBytes, 0);
  const loaded = Math.min(Math.max(progress.loadedBytes, 0), total);
  const ratio = total > 0 ? loaded / total : 0;
  // Never claim 100% before the last byte is in.
  const percent = Math.min(Math.floor(ratio * 100), loaded < total ? 99 : 100);
  const digits = total >= 10 * MB ? 0 : 1;
  const mb = new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return {
    percent: new Intl.NumberFormat(locale, { style: 'percent' }).format(
      percent / 100
    ),
    bytes: `${mb.format(loaded / MB)} / ${mb.format(total / MB)} MB`,
  };
}
